import { defineStore } from 'pinia';
import { comApi } from '@/http/api';
import commissioningApiClient from '@/http/commissioningApiClient';

import { useGlobalMessageStore } from './globalMessageStore';
import type {
  CommissioningDevice,
  OutputConnections,
  CommissionResult,
  CommissionStateResponse,
  Terminal,
  RecommissionPrepareState,
} from '../models/CommissioningDevice';
import { DEFAULT_RECOMMISSION_PREPARE_STATE } from '../models/CommissioningDevice';
import {
  GROUP_INDEXES,
  PHASE,
  SEARCH_METER_CODE,
  SEARCH_METER_INTERVAL_MS,
  SEARCH_METER_TIMEOUT_MS,
  COMMISSION_LOG_INTERVAL_MS,
  COMMISSION_LOG_TIMEOUT_MS,
  UPLOAD_CONFIG_INTERVAL_MS,
  UPLOAD_CONFIG_TIMEOUT_MS,
  COMMISSION_CONFIG_STATUS,
  COMMISSION_STATUS,
  COMMISSION_RESULT_STATUS,
  COMMISSION_RESULT_LOG_INTERVAL_MS,
  COMMISSION_RESULT_LOG_TIMEOUT_MS,
  COMMISSION_RESULT_COMMAND_RESEND_INTERVAL_MS,
  COMMISSION_CONFIG_STATUS_TEXT,
  COMMISSION_STATUS_TEXT,
  COMMISSION_RESULT_STATUS_TEXT,
  COMMISSION_RESULT_VERDICT,
  TERMINALS_LIST,
  MQTT_COMMAND,
  RECOMMISSION_PREPARE_POLL_INTERVAL_MS,
  RECOMMISSION_PREPARE_POLL_TIMEOUT_MS,
} from '../utils/constants';
import {
  deriveSoftwareUpdateRequirement,
  normalizeSoftwareState,
} from '../utils/software-update';
import pWaitFor from 'p-wait-for';
import {
  pollWithPeriodicResend,
  type PollOutcome,
  type SendOutcome,
} from '../utils/pollWithPeriodicResend';
import type { AxiosResponse } from 'axios';
import { isEmptyObject } from '@/utils/object';
import { useDeviceSpecificationStore } from './deviceSpecificationStore';
import { MeterAppState } from '../models/MeterAppState';
import { allConnectionsDecommissioned } from '../utils/allConnectionsDecommissioned';

const NOT_STARTED_TEXT = 'Not started yet';
const TEST_COMMISSION_IDS = ['MK2_TEST_001'];

const __CommissionResultHasFailedConnection = (results: CommissionResult) => {
  return (
    !isEmptyObject(results) &&
    Object.values(results).some((result) => result.verdict === COMMISSION_RESULT_VERDICT.FAIL)
  );
};

/**
 * Purpose of useCommissioningDeviceStore:
 * - Stores the physical device information, including serial number,
 *   wifi status, software update status, and reported connections.
 */
export const useCommissioningDeviceStore = defineStore('commissioningDevice', {
  persist: true,
  state: (): CommissioningDevice => ({
    serialNumber: '',
    wifiStatus: {
      connected: false,
      ssid: '',
    },
    softwareUpdate: {
      needSoftwareUpdate: false,
      forceUpdate: false,
    },
    userHasAccess: null,
    outputConnections: {} as OutputConnections,
    terminalsStateFound: false,
    commissioningId: null,
    isUploadConfig: true,
    configUploadLog: {
      status: null,
      startPercent: 0,
      text: NOT_STARTED_TEXT,
    },
    commissioningLog: {
      status: null,
      startPercent: 0,
      text: NOT_STARTED_TEXT,
    },
    commissionResultStatus: {
      status: null,
      startPercent: 0,
      text: NOT_STARTED_TEXT,
      results: null,
    },
    recommissionPrepare: { ...DEFAULT_RECOMMISSION_PREPARE_STATE },
  }),

  getters: {
    connectedTerminalsArray: (state: CommissioningDevice) => {
      const connectedTerminalsGroupedByPhase = Object.values(PHASE).map((phase) =>
        GROUP_INDEXES.filter(
          (groupIndex) => state.outputConnections[`${phase}-${groupIndex + 1}` as Terminal]?.ready,
        ),
      );

      return connectedTerminalsGroupedByPhase;
    },
  },

  actions: {
    resetRecommissionPrepare() {
      this.recommissionPrepare = { ...DEFAULT_RECOMMISSION_PREPARE_STATE };
    },

    updateRecommissionPrepare(partial: Partial<RecommissionPrepareState>) {
      this.recommissionPrepare = { ...this.recommissionPrepare, ...partial };
    },

    /**
     * Call backend to look for solshare unit with serial number entered.
     * @returns {number} - raw numeric search code from backend, or SEARCH_METER_CODE.NOT_FOUND
     */
    async findSolShare(
      serialNumber: string,
      options?: { preserveRecommissionPrepare?: boolean },
    ) {
      const getSolShareEndpoint = comApi.getSolShare(serialNumber);

      const preservedPrepare = options?.preserveRecommissionPrepare
        ? { ...this.recommissionPrepare }
        : null;

      this.$reset();
      this.serialNumber = serialNumber;
      useDeviceSpecificationStore().beginInverterSpecContext(serialNumber);

      if (preservedPrepare) {
        this.recommissionPrepare = preservedPrepare;
      }

      try {
        const searchResultCode = await pWaitFor(
          async () => {
            const response = await commissioningApiClient.get(getSolShareEndpoint);
            updateDeviceInfo(this, response);
            const code = response.data.code;

            return shouldContinuePolling(code)
              ? false // return false to continue polling until timeout
              : pWaitFor.resolveWith(code); // else return the search result code and exit waitFor
          },
          {
            interval: SEARCH_METER_INTERVAL_MS,
            timeout: SEARCH_METER_TIMEOUT_MS,
          },
        );

        if (
          searchResultCode === SEARCH_METER_CODE.READY_TO_GO ||
          searchResultCode === SEARCH_METER_CODE.NOT_COMMISSIONED
        ) {
          await this.refreshSoftwareUpdateRequirement();
        }

        return searchResultCode;
      } catch (err) {
        console.log('Error finding solshare unit', err);
        return SEARCH_METER_CODE.OFFLINE;
      }
    },

    resetSoftwareUpdateState() {
      this.softwareUpdate = { needSoftwareUpdate: false, forceUpdate: false };
    },

    /**
     * Derive need/force from v3 GET /solshares/{serial}/software only.
     * On failure: fail closed (need + force) — do not use legacy meter flags.
     */
    async refreshSoftwareUpdateRequirement() {
      try {
        const { data } = await commissioningApiClient.get(
          comApi.getSolshareSoftware(this.serialNumber),
        );
        this.softwareUpdate = deriveSoftwareUpdateRequirement(normalizeSoftwareState(data));
      } catch (error) {
        console.error('Error reading SolShare software state', error);
        this.softwareUpdate = { needSoftwareUpdate: true, forceUpdate: true };
      }
    },

    async retrySoftwareUpdate() {
      await this.refreshSoftwareUpdateRequirement();
    },

    /**
     * Use the same commission state endpoint as the one polling for commission log,
     * but this function focus on the terminals status only (ie. "phases" and "connections" attributes in the response)
     */
    async fetchTerminalsState() {
      this.terminalsStateFound = false;
      const commissionStateEndpoint = comApi.commissionStatus(this.serialNumber);
      try {
        const response: AxiosResponse<CommissionStateResponse> = await commissioningApiClient.get(
          commissionStateEndpoint,
        );

        const isDeviceCommunicating = Object.keys(response.data).length > 0;
        const includeAllTerminalsState =
          Object.keys(response.data?.connections ?? {}).length === TERMINALS_LIST.length;

        if (isDeviceCommunicating && includeAllTerminalsState) {
          this.terminalsStateFound = true;
          updateOutputConnections(this, response);
          useDeviceSpecificationStore().resetDraftAllocations();
          checkForPhaseError(response);
        }
      } catch (error) {
        console.error('Error while fetching device state', error);
        throw new Error('Error while fetching device state');
      }
    },

    resetCommissionProcess(
      resetFlags: Array<keyof CommissioningDevice> = [
        // Default to reset all the commission process related states
        'configUploadLog',
        'commissioningLog',
        'commissionResultStatus',
      ],
    ) {
      if (resetFlags.includes('configUploadLog')) {
        // Skipping the reset of the configUploadLog means that the user wants
        // to continue with the same commissioning configuration.
        this.configUploadLog.status = null;
        this.configUploadLog.startPercent = 0;
        this.configUploadLog.text = NOT_STARTED_TEXT;
      }

      if (resetFlags.includes('commissioningLog')) {
        // A new commissioningId will be created each time the commission is started anyway...
        // Resetting just the commissionLog means that the user wants to start a new commission.
        this.commissioningId = null;
        this.commissioningLog.status = null;
        this.commissioningLog.startPercent = 0;
        this.commissioningLog.text = NOT_STARTED_TEXT;
      }

      if (resetFlags.includes('commissionResultStatus')) {
        // Do NOT clear the commissioningId here, as it is needed when getting the commission result
        // Use case: Resetting ONLY the commissionResultStatus indicates that the user
        // wants to retrieve the previous commissioning result again.
        this.commissionResultStatus.status = null;
        this.commissionResultStatus.startPercent = 0;
        this.commissionResultStatus.text = NOT_STARTED_TEXT;
        this.commissionResultStatus.results = null;
      }
    },

    setUploadConfigState(status: COMMISSION_CONFIG_STATUS) {
      const _PERCENTAGE = {
        [COMMISSION_CONFIG_STATUS.PENDING]: 50,
        [COMMISSION_CONFIG_STATUS.SUCCESS]: 100,
        [COMMISSION_CONFIG_STATUS.FAILED]: 0,
      };

      this.configUploadLog.status = status;
      this.configUploadLog.startPercent = _PERCENTAGE[status];
      this.configUploadLog.text = COMMISSION_CONFIG_STATUS_TEXT[status];
    },

    async updateConfig(serialNumber: string, onSuccess: () => void) {
      this.serialNumber = serialNumber;
      this.configUploadLog.startPercent = 0;
      const commandEndpoint = comApi.commissionCommand(this.serialNumber);
      let success = false;

      try {
        const response = await commissioningApiClient.post(commandEndpoint, {
          command: MQTT_COMMAND.UPDATE_CONFIG,
        });
        success = response.data?.success; // Boolean
      } catch (error) {
        this.setUploadConfigState(COMMISSION_CONFIG_STATUS.FAILED);
        throw new Error('Error updating the configuration. Please try again.');
      } finally {
        // Initial state when the config update is just started
        this.setUploadConfigState(
          success ? COMMISSION_CONFIG_STATUS.PENDING : COMMISSION_CONFIG_STATUS.FAILED,
        );

        if (this.configUploadLog.status !== COMMISSION_CONFIG_STATUS.FAILED && onSuccess)
          onSuccess();
      }

      return this.configUploadLog.status;
    },

    async pollUpdateConfigStatus(serialNumber: string) {
      this.serialNumber = serialNumber;

      await pWaitFor(
        async () => {
          try {
            const configStatusEndpoint = comApi.commissionConfigStatus(this.serialNumber);

            const response = await commissioningApiClient.get(configStatusEndpoint);
            this.configUploadLog.status = response?.data?.status as COMMISSION_CONFIG_STATUS;
          } catch (error) {
            // The listening API failed but perhaps the config update is still in progress
            // so we continue to listen for the status...
            console.error('Error getting config status');
          }

          this.setUploadConfigState(this.configUploadLog.status as COMMISSION_CONFIG_STATUS);
          return this.configUploadLog.status !== COMMISSION_CONFIG_STATUS.PENDING;
        },
        {
          interval: UPLOAD_CONFIG_INTERVAL_MS,
          timeout: UPLOAD_CONFIG_TIMEOUT_MS,
        },
      );
    },

    setCommissioningLogState(
      status: COMMISSION_STATUS,
      completedTests: number = 0,
      totalTests: number = 0,
    ) {
      const _PERCENTAGE = {
        [COMMISSION_STATUS.READY]: 0,
        [COMMISSION_STATUS.FAILED]: 0,
        [COMMISSION_STATUS.COMPLETE]: 100,
        [COMMISSION_STATUS.RUNNING]: (completedTests: number, totalTests: number) => {
          if (totalTests === 0) return 0;
          return Math.min((completedTests / totalTests) * 100, 100);
        },
      };

      this.commissioningLog.status = status;
      if (status === COMMISSION_STATUS.RUNNING) {
        this.commissioningLog.startPercent = _PERCENTAGE[COMMISSION_STATUS.RUNNING](
          completedTests,
          totalTests,
        );
        this.commissioningLog.text = `Testing ${completedTests} of ${totalTests} output connections`;
      } else {
        this.commissioningLog.startPercent = _PERCENTAGE[status] as number;
        this.commissioningLog.text = COMMISSION_STATUS_TEXT[status];
      }
    },

    async startCommission(serialNumber: string, onSuccess?: () => void) {
      this.serialNumber = serialNumber;
      this.commissioningLog.startPercent = 0;

      this.commissionResultStatus.startPercent = 0;
      this.commissionResultStatus.status = null;
      this.commissionResultStatus.text = '';

      const commandEndpoint = comApi.commissionCommand(this.serialNumber);

      // Pick only the connected output terminals
      const tests = Object.fromEntries(
        Object.entries(this.outputConnections)
          .filter((entry) => entry[1].ready)
          .map(([key]) => [key, {}]),
      );

      // Generate a unique commissioning ID
      // Save the Commission ID immediately so it's available even if the API call fails
      this.commissioningId = crypto.randomUUID();
      let success = false;

      try {
        const response = await commissioningApiClient.post(commandEndpoint, {
          command: MQTT_COMMAND.COMMISSION_START,
          data: {
            commissioning_id: this.commissioningId,
            tests,
          },
        });

        // Initial state when the commission is just started
        success = response.data?.success;
      } catch (error) {
        this.setCommissioningLogState(COMMISSION_STATUS.FAILED);
        throw new Error('Error starting the commissioning process. Please try again.');
      }
      this.setCommissioningLogState(success ? COMMISSION_STATUS.READY : COMMISSION_STATUS.FAILED);
      if (this.commissioningLog.status !== COMMISSION_STATUS.FAILED && onSuccess) await onSuccess();

      return this.commissioningLog.status;
    },

    async pollCommissionState(serialNumber: string) {
      this.serialNumber = serialNumber;

      await pWaitFor(
        async () => {
          try {
            const commissionStateEndpoint = comApi.commissionStatus(this.serialNumber);
            const response = await commissioningApiClient.get(commissionStateEndpoint);

            // Check if the response commissioning_id matches our stored commissioningId
            // Before processing the response, ensure we're getting data for the correct commission
            const responseCommissioningId = response.data?.commissioning_id;

            // If we have a stored commissioningId, the response must include it and it must match
            if (this.commissioningId) {
              const validResponse =
                responseCommissioningId &&
                (responseCommissioningId === this.commissioningId ||
                  TEST_COMMISSION_IDS.includes(responseCommissioningId));

              if (!validResponse) return false;
            }

            // Only process the response if commissioning_id matches (or if we don't have one stored)
            this.commissioningLog.status = response.data?.state as COMMISSION_STATUS;

            if (this.commissioningLog.status === COMMISSION_STATUS.RUNNING) {
              const hasCorrectProgressDataFormat =
                response.data?.progress?.completed && // It should not be null or undefined
                Array.isArray(response.data?.progress?.completed) && // It should be an array
                response.data?.progress?.completed.length === 2; // It should have exactly two array elements

              if (hasCorrectProgressDataFormat) {
                const totalTests = response.data?.progress?.completed[1];
                if (totalTests > 0) {
                  // Do not update the state, if the total number of tests is or becomes 0
                  // This is just to avoid the progress bar from going backwards (not nice for the user to see)
                  // So listen again for the next response...
                  const completedTests = response.data?.progress?.completed[0];
                  this.setCommissioningLogState(
                    COMMISSION_STATUS.RUNNING,
                    completedTests,
                    totalTests,
                  );
                }
              }
            } else {
              this.setCommissioningLogState(this.commissioningLog.status as COMMISSION_STATUS);
            }
          } catch (error) {
            // The listening API failed but perhaps the commission is still in progress
            // so we continue to listen for the status...
            console.error('Error getting the commissioning process state');
          }

          return ![null, COMMISSION_STATUS.READY, COMMISSION_STATUS.RUNNING].includes(
            this.commissioningLog.status,
          );
        },
        {
          interval: COMMISSION_LOG_INTERVAL_MS,
          timeout: COMMISSION_LOG_TIMEOUT_MS,
        },
      );
    },

    setCommissionResultStatusState(
      status: COMMISSION_RESULT_STATUS,
      pendingPercentage: number = 50,
    ) {
      const _PERCENTAGE = {
        [COMMISSION_RESULT_STATUS.PENDING]: pendingPercentage,
        [COMMISSION_RESULT_STATUS.SUCCESS]: 100,
        [COMMISSION_RESULT_STATUS.FAILED]: 0,
      };

      this.commissionResultStatus.status = status;
      this.commissionResultStatus.startPercent = _PERCENTAGE[status];
      this.commissionResultStatus.text = COMMISSION_RESULT_STATUS_TEXT[status];
    },

    /**
     * POST `commission-get-results` and poll GET results until terminal state or timeout.
     * Re-sends the command periodically while the poll stays pending (lost command recovery).
     */
    async fetchCommissionResultWithPolling(serialNumber: string, commissionId: string) {
      this.serialNumber = serialNumber;
      this.commissioningId = commissionId;
      const commandEndpoint = comApi.commissionCommand(this.serialNumber);

      const sendCommand = async (): Promise<SendOutcome> => {
        try {
          const response = await commissioningApiClient.post(commandEndpoint, {
            command: MQTT_COMMAND.COMMISSION_GET_RESULTS,
          });
          const success = response.data?.success as boolean | undefined;
          if (success === false) {
            this.setCommissionResultStatusState(COMMISSION_RESULT_STATUS.FAILED);
            return 'abort';
          }
          this.setCommissionResultStatusState(COMMISSION_RESULT_STATUS.PENDING, 0);
          return 'continue';
        } catch (error) {
          this.setCommissionResultStatusState(COMMISSION_RESULT_STATUS.FAILED);
          throw new Error('Error getting the commission result. Please try again.');
        }
      };

      const pollOnceResult = async (): Promise<PollOutcome> => {
        try {
          const commissionResultStatusEndpoint = comApi.commissionResultStatus(
            this.serialNumber,
            this.commissioningId!,
          );
          const response = await commissioningApiClient.get(commissionResultStatusEndpoint);
          const responseStatus = response.data?.state as COMMISSION_RESULT_STATUS | undefined;

          const status = responseStatus ?? COMMISSION_RESULT_STATUS.PENDING;
          const isSuccess = status === COMMISSION_RESULT_STATUS.SUCCESS;

          if (isSuccess) {
            this.commissionResultStatus.results = response.data?.results as CommissionResult;
          }

          this.commissionResultStatus.status =
            isSuccess &&
            __CommissionResultHasFailedConnection(
              this.commissionResultStatus.results as CommissionResult,
            )
              ? COMMISSION_RESULT_STATUS.FAILED
              : status;
        } catch (error) {
          console.error('Error getting commission result status');
          this.commissionResultStatus.status =
            this.commissionResultStatus.status ?? COMMISSION_RESULT_STATUS.PENDING;
        }

        this.setCommissionResultStatusState(
          this.commissionResultStatus.status as COMMISSION_RESULT_STATUS,
        );

        const terminal = this.commissionResultStatus.status as COMMISSION_RESULT_STATUS;
        if (terminal === COMMISSION_RESULT_STATUS.SUCCESS) return 'success';
        if (terminal === COMMISSION_RESULT_STATUS.FAILED) return 'fail';
        return 'pending';
      };

      await pollWithPeriodicResend({
        send: sendCommand,
        pollOnce: pollOnceResult,
        pollIntervalMs: COMMISSION_RESULT_LOG_INTERVAL_MS,
        timeoutMs: COMMISSION_RESULT_LOG_TIMEOUT_MS,
        resendIntervalMs: COMMISSION_RESULT_COMMAND_RESEND_INTERVAL_MS,
      });
    },

    async completeCommission(serialNumber: string, onSuccess: () => void, onFailure?: () => void) {
      /**
       * At the moment, the complete commission command runs automatically once the commission
       * results indicate success, so this check rarely fails.
       *
       * However, it becomes valuable once commissioners are able to trigger this command manually
       * in the future. This validation ensures that before the complete commission command is executed,
       * the following conditions are met:
       * 1. Commission results are present.
       * 2. The results confirm that no connections have failed.
       */
      if (
        !this.commissionResultStatus.results ||
        __CommissionResultHasFailedConnection(this.commissionResultStatus.results)
      ) {
        if (onFailure) onFailure();
        throw new Error('Cannot complete the commissioning process. Please try again.');
      }

      this.serialNumber = serialNumber;
      const commandEndpoint = comApi.commissionCommand(this.serialNumber);

      try {
        const response = await commissioningApiClient.post(commandEndpoint, {
          command: MQTT_COMMAND.COMMISSION_COMPLETE,
          data: {
            connections: constructCommissionCompletePayload(this.commissionResultStatus.results),
          },
        });

        if (response.data?.success) {
          if (onSuccess) await onSuccess();
        } else {
          if (onFailure) await onFailure();
          throw new Error();
        }
      } catch (error) {
        throw new Error('Error completing the commission. Please try again.');
      }
    },

    /**
     * POST an MQTT command to the device. Throws when the API fails or success is false.
     */
    async sendMeterCommand(
      serialNumber: string,
      command: (typeof MQTT_COMMAND)[keyof typeof MQTT_COMMAND],
    ) {
      this.serialNumber = serialNumber;
      const commandEndpoint = comApi.commissionCommand(serialNumber);

      try {
        const response = await commissioningApiClient.post(commandEndpoint, { command });
        if (!response.data?.success) {
          throw new Error(`Command "${command}" was not accepted for serial [${serialNumber}]`);
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('was not accepted')) {
          throw error;
        }
        throw new Error(`Error sending command "${command}" for serial [${serialNumber}]`);
      }
    },

    /**
     * Poll GET /v3/meters/:serial/state until `app` matches the expected MeterAppState.
     * @param onPoll Optional callback after each poll attempt (success or error).
     */
    async pollUntilAppState(
      serialNumber: string,
      expected: MeterAppState,
      onPoll?: () => void,
    ) {
      this.serialNumber = serialNumber;
      const endpoint = comApi.getMeterAppState(serialNumber);

      await pWaitFor(
        async () => {
          try {
            const response = await commissioningApiClient.get(endpoint);
            onPoll?.();
            return response.data?.app === expected;
          } catch (error) {
            console.error('Error polling meter app state', error);
            onPoll?.();
            return false;
          }
        },
        {
          interval: RECOMMISSION_PREPARE_POLL_INTERVAL_MS,
          timeout: RECOMMISSION_PREPARE_POLL_TIMEOUT_MS,
        },
      );
    },

    /**
     * Poll GET /v2/commission/meter/:serial/state until every connection is DECOMMISSIONED.
     * @param onPoll Optional callback after each poll attempt (success or error).
     */
    async pollUntilAllConnectionsDecommissioned(
      serialNumber: string,
      onPoll?: () => void,
    ) {
      this.serialNumber = serialNumber;
      const endpoint = comApi.commissionStatus(serialNumber);

      await pWaitFor(
        async () => {
          try {
            const response = await commissioningApiClient.get(endpoint);
            onPoll?.();
            return allConnectionsDecommissioned(response.data?.connections);
          } catch (error) {
            console.error('Error polling commission connections for decommission', error);
            onPoll?.();
            return false;
          }
        },
        {
          interval: RECOMMISSION_PREPARE_POLL_INTERVAL_MS,
          timeout: RECOMMISSION_PREPARE_POLL_TIMEOUT_MS,
        },
      );
    },
  },
});

// Helper functions
const updateDeviceInfo = (
  store: ReturnType<typeof useCommissioningDeviceStore>,
  response: AxiosResponse,
) => {
  const {
    data: { connected, ssid, userHasAccess },
  } = response;
  store.wifiStatus = { connected, ssid };
  // needSoftwareUpdate / forceUpdate come from v3 GET after find (not legacy meter flags)
  store.userHasAccess = typeof userHasAccess === 'boolean' ? userHasAccess : null;

  return;
};

const updateOutputConnections = (
  store: ReturnType<typeof useCommissioningDeviceStore>,
  response: AxiosResponse<CommissionStateResponse>,
) => {
  const { connections: respondedConnections } = response.data;

  const dataToStore: OutputConnections = TERMINALS_LIST.reduce((acc, terminal) => {
    acc[terminal] = respondedConnections[terminal] ?? { ready: false, state: '' };
    return acc;
  }, {} as OutputConnections);

  store.outputConnections = dataToStore;
  return;
};

const constructCommissionCompletePayload = (commissionResult: CommissionResult) => {
  return TERMINALS_LIST.reduce((acc, terminal_key) => {
    const testPassed = commissionResult[terminal_key]?.verdict === COMMISSION_RESULT_VERDICT.PASS;
    const terminalState = testPassed
      ? { enabled: true, commissioned: true }
      : { enabled: false, commissioned: false };

    acc[terminal_key] = terminalState;
    return acc;
  }, {} as Record<Terminal, { enabled: boolean; commissioned: boolean }>);
};

const checkForPhaseError = (response: AxiosResponse<CommissionStateResponse>) => {
  const { phases: respondedPhases } = response.data;
  useGlobalMessageStore().checkPhaseError(respondedPhases);
};

const shouldContinuePolling = (searchResultCode: number) => {
  return searchResultCode && searchResultCode === SEARCH_METER_CODE.OFFLINE;
};
